import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { EnvironmentOutlined, ExperimentOutlined } from "@ant-design/icons";
import {
  getEnvironmentFields,
  getEnvSpecsData,
  getEnvPoints,
} from "../../../../actions/specManager";
import {
  parseSpecDetail,
  formatSpec,
  buildEnvironmentIdentifierFields,
  mapEnvPointToLocationRow,
  filterLocationRowsBySpecFilter,
  ENV_SPEC_MANAGER_TAB_KEYS,
  SPEC_FILTER_VALUES,
  canViewSpecManager,
} from "../utils/utils";
import { DiscardChangesModal } from "../shared/specManagerUi";
import LocationSpecListPanel from "./LocationSpecListPanel";
import LocationSpecEditor from "./LocationSpecEditor";
import EnvTestSpecManager from "./EnvTestSpecManager";
import "../SpecManager.css";

/** Set to true to show Location Spec / Test Spec tab switches in the Environment Spec Manager. */
const SHOW_ENV_SPEC_MANAGER_TABS = false;

const TAB_CONFIG = [
  { key: ENV_SPEC_MANAGER_TAB_KEYS.LOCATION, label: "Location Spec", icon: <EnvironmentOutlined /> },
  { key: ENV_SPEC_MANAGER_TAB_KEYS.TEST, label: "Test Spec", icon: <ExperimentOutlined /> },
];

export default function EnvironmentSpecManager({ customer, onEditModeChange }) {
  const [identifierFields, setIdentifierFields] = useState([]);
  const [activeTab, setActiveTab] = useState(ENV_SPEC_MANAGER_TAB_KEYS.TEST);
  const [locationRows, setLocationRows] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [activeFilter, setActiveFilter] = useState(SPEC_FILTER_VALUES.ALL);
  const [loading, setLoading] = useState(true);
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isAddingSpec, setIsAddingSpec] = useState(false);
  const [specRows, setSpecRows] = useState([]);
  const [loadingEditData, setLoadingEditData] = useState(false);
  const [specDataChanged, setSpecDataChanged] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState(null);
  const [isEditingTestSpec, setIsEditingTestSpec] = useState(false);
  const [isEnvironmentFieldsReady, setIsEnvironmentFieldsReady] = useState(false);

  const abortControllerRef = useRef(null);
  const identifierFieldsRef = useRef([]);

  const fetchLocationList = async ({
    filter = activeFilter,
    search = searchValue,
    scrollReset = false,
  } = {}) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);

    const response = await getEnvPoints(
      {
        search,
        filter: filter || undefined,
      },
      abortController.signal,
      customer,
    );

    if (response.canceled) return;

    const fields = identifierFieldsRef.current;

    if (response.success) {
      const normalizedRows = response.locations.map((point) => (
        mapEnvPointToLocationRow(point, fields)
      ));
      setLocationRows(filterLocationRowsBySpecFilter(normalizedRows, filter));
    } else {
      setLocationRows([]);
      toast.error(response.message || "Failed to load locations.");
    }

    setLoading(false);
    if (scrollReset) {
      setSearchValue(search);
      setActiveFilter(filter);
    }
  };

  useEffect(() => {
    let canceled = false;

    setIdentifierFields([]);
    identifierFieldsRef.current = [];
    setSearchValue("");
    setActiveFilter(SPEC_FILTER_VALUES.ALL);
    setLocationRows([]);
    setLoading(true);
    setIsEnvironmentFieldsReady(false);

    getEnvironmentFields(null, customer).then((result) => {
      if (canceled) return;

      if (!result.success) {
        if (!result.canceled && result.message) {
          if (result.isWarning) {
            toast.warning(result.message);
          } else {
            toast.error(result.message);
          }
        }
        setLoading(false);
        return;
      }

      const fields = buildEnvironmentIdentifierFields(result);
      setIdentifierFields(fields);
      identifierFieldsRef.current = fields;
      setIsEnvironmentFieldsReady(true);
      if (SHOW_ENV_SPEC_MANAGER_TABS && activeTab === ENV_SPEC_MANAGER_TAB_KEYS.LOCATION) {
        fetchLocationList({ scrollReset: true });
      } else {
        setLoading(false);
      }
    });

    return () => {
      canceled = true;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [customer.company, customer.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onEditModeChange) {
      onEditModeChange(isEditingLocation || isEditingTestSpec);
    }
  }, [isEditingLocation, isEditingTestSpec, onEditModeChange]);

  const handleTabChange = (tabKey) => {
    if (tabKey === activeTab) return;
    setActiveTab(tabKey);
    if (tabKey === ENV_SPEC_MANAGER_TAB_KEYS.LOCATION) {
      fetchLocationList({ scrollReset: true });
    }
  };

  const handleLocationRowClick = async (event, rowIndex, status) => {
    event.preventDefault();
    const locationRow = locationRows[rowIndex];
    setSelectedLocation(locationRow);
    setIsAddingSpec(status === "pending");
    setIsEditingLocation(true);
    setLoadingEditData(true);
    setSpecDataChanged(false);

    const response = await getEnvSpecsData({
      location_id: locationRow.location_id,
      page: "1",
    }, false, null, customer);

    if (response.success && response.files) {
      const parsedRows = response.files.map((specRow) => parseSpecDetail(specRow));
      setSpecRows(parsedRows);
    } else {
      toast.error("Failed to get spec data.");
      setIsEditingLocation(false);
    }
    setLoadingEditData(false);
  };

  const handleSaveSpec = async (formattedSpecs) => {
    const response = await getEnvSpecsData({
      location_id: selectedLocation.location_id,
      specs: formattedSpecs,
      page: "1",
    }, true, null, customer);

    if (response.success) {
      toast.success("Specs and status updated.");
      await fetchLocationList();
      return true;
    }
    toast.error("Failed to save specs.");
    return false;
  };

  const handleSaveTestList = async (updatedSpecRows) => {
    const formattedSpecs = formatSpec(updatedSpecRows);
    if (formattedSpecs.some((item) => typeof item === "string")) {
      toast.error(formattedSpecs.find((item) => typeof item === "string"));
      return false;
    }
    const response = await getEnvSpecsData({
      location_id: selectedLocation.location_id,
      specs: formattedSpecs,
      action: "update_test_list",
      page: "1",
    }, true, null, customer);

    if (response.success) {
      toast.success("Test list updated.");
      return true;
    }
    toast.error("Failed to update test list.");
    return false;
  };

  const exitLocationEditor = () => {
    setIsEditingLocation(false);
    setSelectedLocation(null);
    setSpecRows([]);
    setSpecDataChanged(false);
    setIsAddingSpec(false);
  };

  const handleBackFromEditor = () => {
    if (specDataChanged) {
      setPendingCloseAction(() => exitLocationEditor);
      setShowDiscardModal(true);
    } else {
      exitLocationEditor();
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardModal(false);
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
  };

  if (!canViewSpecManager()) {
    return null;
  }

  const showLocationSpec = SHOW_ENV_SPEC_MANAGER_TABS
    && activeTab === ENV_SPEC_MANAGER_TAB_KEYS.LOCATION;
  const showTestSpec = !SHOW_ENV_SPEC_MANAGER_TABS
    || activeTab === ENV_SPEC_MANAGER_TAB_KEYS.TEST;

  return (
    <div className="SpecManager_Root">
      {SHOW_ENV_SPEC_MANAGER_TABS && !isEditingLocation && !isEditingTestSpec && (
        <div className="SpecManager_Tabs">
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`SpecManager_TabButton${activeTab === tab.key ? " SpecManager_TabButton--active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      )}

      {showLocationSpec && isEnvironmentFieldsReady && isEditingLocation && (
        <LocationSpecEditor
          customer={customer}
          identifierFields={identifierFields}
          locationRow={selectedLocation}
          isAddingSpec={isAddingSpec}
          specRows={specRows}
          loading={loadingEditData}
          specDataChanged={specDataChanged}
          onSpecRowsChange={setSpecRows}
          onSpecDataChanged={setSpecDataChanged}
          onSaveSpec={handleSaveSpec}
          onSaveTestList={handleSaveTestList}
          onBack={handleBackFromEditor}
        />
      )}

      {showLocationSpec && isEnvironmentFieldsReady && !isEditingLocation && (
        <LocationSpecListPanel
          locationRows={locationRows}
          identifierFields={identifierFields}
          loading={loading}
          loadingMore={false}
          searchValue={searchValue}
          activeFilter={activeFilter}
          onSearchChange={(value) => {
            setSearchValue(value);
            fetchLocationList({ search: value, scrollReset: true });
          }}
          onFilterChange={(filter) => {
            setActiveFilter(filter);
            fetchLocationList({ filter, scrollReset: true });
          }}
          onLocationRowClick={handleLocationRowClick}
          onLoadMore={() => {}}
        />
      )}

      {showTestSpec && (
        <EnvTestSpecManager
          customer={customer}
          identifierFields={identifierFields}
          onEditModeChange={setIsEditingTestSpec}
        />
      )}

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={() => {
          setShowDiscardModal(false);
          setPendingCloseAction(null);
        }}
        onConfirm={handleDiscardConfirm}
      />
    </div>
  );
}
